/**
 * Where each editable field appears on the public site, and in what order (D-241).
 *
 * <b>The screen used to derive this from the key.</b> Blocks arrived ordered
 * alphabetically by a string Tonya never sees, grouped by guessing at the first
 * dotted segment — so the editor opened on *Competitive teams*, ran the About
 * page very nearly in reverse against the page, and reliably put
 * `home.point1.body` above `home.point1.title`: a paragraph above the heading it
 * sits under. Three sections rendered as the raw fragments `announcement`,
 * `register` and `import`, and a fourth said "Good to know" where the page says
 * "Need to know".
 *
 * The instinct that produced it was a good one, and is worth stating because it
 * is the one a future reader will have too: *the key already encodes the
 * grouping, so do not keep a second copy that can drift.* It missed that the key
 * encodes the grouping and **not the order** — and the order is the whole
 * difficulty of finding one field among sixty-five. It had drifted anyway.
 *
 * <b>So the second copy is admitted, and made checkable rather than asserted.</b>
 * `tests/CDA.IntegrationTests/ContentManifestTests.cs` pins set equality in
 * both directions: every content
 * block appears here exactly once, and no entry here names a block that does not
 * exist. That is the drift that actually happened. What it cannot check is
 * *ordering* drift — someone reorders `AboutView.tsx` and this file does not
 * follow. On a five-page site that changes rarely that is a nuisance, not a lie.
 *
 * <b>The better answer, if this ever becomes a problem:</b> mark each render site
 * with `data-content-key` and read the order out of the rendered DOM, which
 * deletes this file entirely. That was costed and refused as too large for what
 * it buys — see `docs/specs/2026-08-20-content-editing.md`, O6.
 *
 * It lives client-side because the order is a fact about `AboutView.tsx` and
 * `HomeView.tsx`, not about the database. A server-side copy would go stale
 * exactly as the label map did.
 */
export type ContentSection = {
  /** What the page calls this, never what the key calls it. */
  heading: string
  /** A line under the heading, where the section needs explaining. */
  note?: string
  /** In the order they appear on the page, reading downwards. */
  keys: string[]
  /**
   * Which previewable pages this section's words appear on (D-260).
   *
   * <b>Omitted means "not on any page", and those sections are never filtered
   * out.</b> The parent portal's billing link and the import screen's notes are
   * editable and belong to no public page, so a page filter that hid them would
   * make them unreachable — which `UNPLACED_HEADING` below exists precisely to
   * prevent, and would be a worse failure than the clutter being removed.
   *
   * The paths are the preview frame's, so they are the app's routes for the
   * public pages: `/site` rather than `/`, because signed in `/` is the
   * calendar.
   */
  pages?: string[]
}

/** Every page the preview can show. Sections listing all three follow her. */
const EVERY_PAGE = ['/site', '/classes', '/about']

/**
 * Sections in the order Tonya meets them, which is the order a visitor does:
 * the homepage from the top, then the classes page, then About, then the
 * practical sections, then the things that are not pages.
 */
export const CONTENT_SECTIONS: ContentSection[] = [
  {
    heading: 'Homepage',
    pages: ['/site'],
    keys: [
      'home.hero.headline',
      'home.hero.subhead',
      'home.hero.image',
      'home.welcome.body',
      'home.point1.title',
      'home.point1.body',
      'home.point2.title',
      'home.point2.body',
      'home.point3.title',
      'home.point3.body',
      'home.gallery1.image',
      'home.gallery1.caption',
      'home.gallery2.image',
      'home.gallery2.caption',
      'home.gallery3.image',
      'home.gallery3.caption',
      'home.feed.heading',
      'home.feed.body',
      'home.season',
      'home.cta.body',
    ],
  },
  {
    heading: 'About page',
    pages: ['/about'],
    keys: [
      'about.mission',
      'about.story',
      'about.offering',
      'about.competitive',
      'about.photo1',
      'about.photo2',
      'about.photo3',
      'about.owner1.photo',
      'about.owner1.name',
      'about.owner1.role',
      'about.owner1.bio',
      'about.owner2.photo',
      'about.owner2.name',
      'about.owner2.role',
      'about.owner2.bio',
    ],
  },
  {
    // The page's own words. It said "Good to know" here and "Need to know" on
    // the page, which is a small thing that makes a screen feel like somebody
    // else's.
    heading: 'Need to know',
    note: 'The practical sections at the bottom of the About page.',
    pages: ['/about'],
    keys: [
      'needToKnow.intro',
      'needToKnow.costumeDeposit',
      'needToKnow.recitalFee',
      'needToKnow.costumeBalance',
      'needToKnow.tuitionNote',
      'needToKnow.attire',
      'needToKnow.classDescriptions',
      'needToKnow.closures',
    ],
  },
  {
    heading: 'Classes page',
    note: 'Where the Register button sends families.',
    pages: ['/classes'],
    keys: ['register.url', 'register.note'],
  },
  {
    // Rendered as the bare word `announcement` before this, which tells a
    // reader nothing about the strip across the top of every page.
    heading: 'Announcement banner',
    note: 'Clear the message to hide the banner.',
    // The strip across the top of all three, so it follows her everywhere.
    pages: EVERY_PAGE,
    keys: ['announcement.body', 'announcement.tone', 'announcement.link', 'announcement.linkText'],
  },
  {
    heading: 'Every page',
    note: 'The header, the footer, and how families reach you.',
    pages: EVERY_PAGE,
    keys: [
      'site.logo',
      'home.tagline',
      'studio.phone',
      'studio.email',
      'studio.address',
      'studio.hoursDetail',
      'social.facebook',
      'social.instagram',
    ],
  },
  {
    /*
     * Also not her website — it is the signed-in app (D-259). Placed above the
     * import notes because a family reads it and only Tonya reads those, and
     * below everything public for the same reason the import notes are.
     */
    heading: 'Parent portal',
    note: 'Not part of your website. Where families go for billing, on their account tab.',
    keys: ['portal.jackrabbit.url', 'portal.jackrabbit.note'],
  },
  {
    /*
     * On its own, because of what it is rather than where it sits: this is the
     * text a parent legally agrees to before her under-13 dancer gets a login
     * (D-539), and the version she was shown is stored beside her consent. An
     * edit here changes what future parents agree to and cannot change what
     * past ones did — the note says so, because that is the one thing about
     * this block that is not obvious from reading it.
     */
    heading: 'Dancer logins',
    note: 'Shown to a parent before her dancer gets her own sign-in, on the account screen - the first block for a child under 13, the messaging block at every age. Editing it changes what parents agree to from now on; what people already agreed to is kept as it was.',
    keys: [
      'portal.dancerLogin.consent',
      'portal.dancerLogin.consent.messaging',
      'portal.conversations.heading',
      'portal.conversations.intro',
      'portal.conversations.none',
      'portal.conversations.failed',
      'portal.conversations.needsConsent',
      'portal.conversations.back',
      'portal.dancerLogin.reconsent',
      'portal.dancerLogin.reconsent.agree',
      'portal.dancerLogin.agree',
    ],
  },
  {
    /*
     * The two words on the link a family taps to watch the routine (D-541).
     * The video itself is the teacher's link on the class, not content — this
     * is only what the link is called.
     */
    heading: 'Practice links',
    note: 'The links on a family’s schedule row, shown when the teacher has added a video or the music for that class.',
    keys: ['portal.choreo.link', 'portal.music.link'],
  },
  {
    /*
     * The one-off hello (D-651). No `pages`: it is not a screen anybody can be
     * sent to, it is a dialog that appears once over whatever she opened - so
     * a preview link would be a link to something she cannot make happen again.
     */
    heading: 'First visit',
    note: 'The dialog everybody sees once, the first time they open the app. It points at notifications and fingerprint sign-in, which are both in account settings.',
    keys: ['welcome.heading', 'welcome.body', 'welcome.settings', 'welcome.dismiss'],
  },
  {
    /*
     * The one list of everything waiting on an answer (D-543). Every word on
     * it is here, and the kind names and subject sentences are the ones most
     * worth rewording - a {{field}} in a sentence is filled in by name.
     */
    heading: 'Requests',
    note: 'The Requests screen and the chip on Home. Anything in double braces, like {{names}} or {{dancer}}, is filled in — keep it as written.',
    keys: [
      'asks.heading',
      'asks.intro',
      'asks.waitingOnYou',
      'asks.chase',
      'asks.youAreWaitingOn',
      'asks.none',
      'asks.failed.mine',
      'asks.failed.studio',
      'asks.waitingOn',
      'asks.nobody',
      'asks.raisedBy',
      'asks.late',
      'asks.open',
      'asks.answer.yes',
      'asks.answer.no',
      'asks.answer.why',
      'asks.answer.confirmNo',
      'asks.answer.keep',
      'asks.withdraw',
      'asks.withdraw.confirm',
      'asks.answer.choose',
      'asks.answer.pick',
      'asks.answer.nothing',
      'asks.answer.save',
      'asks.answer.extra',
      'asks.answer.nothingToRepair',
      'asks.chase.call',
      'asks.answer.room',
      'asks.answer.yesRoom',
      'asks.answer.counter',
      'asks.answer.counterSend',
      'asks.answer.counterHint',
      'asks.home.waitingOn',
      'asks.nudges.switch',
      'asks.nudge',
      'asks.nudge.done',
      'asks.nudges.off',
      'asks.home.heading',
      'asks.chip.one',
      'asks.chip.many',
      'asks.kind.cover',
      'asks.kind.coverStandIn',
      'asks.kind.levelUp',
      'asks.kind.bankedLevel',
      'asks.subject.bankedLevel',
      'asks.kind.makeupClaim',
      'asks.kind.privateLesson',
      'asks.kind.privateLessonTime',
      'asks.kind.privateConfirm',
      'asks.kind.payment',
      'asks.kind.longBooking',
      'asks.kind.routineConsent',
      'asks.subject.cover',
      'asks.subject.coverStandIn',
      'asks.subject.levelUp',
      'asks.subject.makeupClaim',
      'asks.subject.privateLesson',
      'asks.subject.privateLessonTime',
      'asks.subject.privateConfirm',
      'asks.subject.payment',
      'asks.subject.timesheet',
      'asks.subject.longBooking',
      'asks.subject.routineConsent',
      'asks.kind.task',
      'asks.kind.timesheet',
      'asks.subject.task',
      'asks.kind.doubleBooked',
      'asks.subject.doubleBooked',
      'asks.task.assign',
      'asks.task.heading',
      'asks.task.who',
      'asks.task.choose',
      'asks.task.title',
      'asks.task.note',
      'asks.task.due',
      'asks.task.send',
      'asks.task.cancel',
      'asks.task.staffFailed',
      'asks.task.hangOn',
      'asks.task.daysBefore',
      'asks.task.anchoredDue',
      'asks.task.typeInstead',
      'asks.task.datesLoading',
      'asks.task.datesFailed',
      'asks.task.noDates',
      'asks.task.anchor',
      'asks.task.anchorMoved',
    ],
  },
  {
    /*
     * Also the signed-in app, and kept apart from the billing block above
     * because it is a different job on a different tab — a family reads this on
     * the schedule, working out what to do about a class she missed (D-276).
     *
     * Several of these take numbers as `{{merge}}` values, which is why the
     * note says so: an edit that drops one leaves the field visible rather than
     * blanking it, and Tonya should know that is deliberate before she meets it.
     */
    heading: 'Makeup classes',
    note: 'On a family’s schedule tab. Anything in double braces, like {{missed}}, is filled in with their own numbers — keep it as written.',
    keys: [
      'portal.makeup.label',
      'portal.makeup.entitlement',
      'portal.makeup.tooLate',
      'portal.makeup.nothingMissed',
      'portal.makeup.nothingThisWeek',
      'portal.makeup.unmarkedCaveat',
      'portal.makeup.unavailable',
      'portal.makeup.book',
      'portal.makeup.booking',
      'portal.makeup.repairs',
      'portal.makeup.repairsUnknown',
      'portal.makeup.provisional',
      'portal.makeup.didNotRun',
      'portal.makeup.cancel',
      'portal.makeup.cancelling',
      'teacher.requests.back',
      'teacher.requests.heading',
      'teacher.requests.intro',
      'teacher.requests.unavailable',
      'teacher.requests.none',
      'portal.privates.label',
      'portal.privates.unavailable',
      'portal.privates.none',
      'portal.actions.label',
      'portal.actions.report',
      'portal.actions.private',
      'teacher.requests.noteLabel',
      'portal.makeup.oneOffProvisional',
      'portal.makeup.oneOffConfirmed',
      'portal.makeup.awayNote',
      // Banking a class before anything is missed (D-611).
      'portal.bank.heading',
      'portal.bank.blurb',
      'portal.bank.needsApproval',
      'portal.bank.stillNeeded',
      'portal.bank.book',
      'portal.bank.booking',
      'portal.bank.booked',
      'portal.bank.onSchedule',
    ],
  },
  {
    /*
     * The conversations screens (M3, D-617). Everything a sender or reader
     * sees that is not somebody's own words: the picker's headings, the
     * household line, the disclosure under the composer (D-626), and every
     * empty and failed state. `{{name}}` and `{{count}}` are filled in.
     */
    heading: 'Messages',
    note: 'The Messages screens: the list, a conversation, and who you can message. Anything in double braces, like {{name}}, is filled in — keep it as written.',
    keys: [
      'messages.intro',
      'messages.new',
      'messages.loading',
      'messages.notHere',
      'messages.list.notHere',
      'messages.failed',
      'messages.empty.title',
      'messages.empty',
      'messages.nothingYet',
      'messages.muted',
      'messages.back',
      'messages.pick.heading',
      'messages.pick.search',
      'messages.pick.failed',
      'messages.pick.none',
      'messages.pick.noMatch',
      'messages.pick.staff',
      'messages.pick.parents',
      'messages.pick.other',
      'messages.group.people',
      'messages.group.private',
      'messages.group.shared',
      'messages.mute',
      'messages.unmute',
      'messages.household.spoken',
      'messages.household.unspoken',
      'messages.household.open',
      'messages.household.start',
      'messages.older',
      'messages.hidden',
      'messages.edited',
      'messages.compose.placeholder',
      'messages.compose.send',
      'messages.disclosure',
      'messages.disclosure.staff',
      'messages.group.new',
      'messages.group.who',
      'messages.group.audience.classParents',
      'messages.group.audience.myParents',
      'messages.group.audience.allTeachers',
      'messages.group.audience.allParents',
      'messages.group.audience.count',
      'messages.group.untick',
      'messages.group.replies',
      'messages.group.mode.private',
      'messages.group.mode.private.note',
      'messages.group.mode.shared',
      'messages.group.mode.shared.note',
      'messages.group.shared.warning',
      'messages.group.title',
      'messages.group.next',
      'messages.group.back',
      'messages.group.start',
      'messages.group.nobody',
      'messages.group.refresh',
      'messages.group.refresh.would',
      'messages.group.refresh.added',
      'messages.group.replyPrivate',
      'messages.group.creatorReply',
      'messages.react',
      'messages.reaction.failed',
      'messages.edit',
      'messages.edit.save',
      'messages.edit.cancel',
      'messages.history.heading',
      'messages.history.original',
      'messages.history.close',
      'messages.history.failed',
      'messages.hide',
      'messages.hide.reason',
      'messages.hide.confirm',
      'messages.hide.cancel',
      'messages.hide.needsReason',
      'messages.hidden.reviewer',
      'messages.mention.hint',
      'messages.mention.you',
      'messages.mention.pick',
      'messages.reaction.thumbsUp',
      'messages.reaction.heart',
      'messages.reaction.clap',
      'messages.reaction.laugh',
      'messages.reaction.sad',
      'messages.reaction.party',
      'messages.pick.dancers',
      'messages.disclosure.parents',
      'messages.review.heading',
      'messages.review.intro',
      'messages.review.none',
      'messages.review.failed',
      'messages.review.readOnly',
      'messages.review.back',
      'messages.review.backToStaff',
      'messages.review.backToPeople',
      'messages.review.intro.guardian',
      'messages.review.notHere',
      'messages.review.theirs',
      'messages.chip.label',
      'messages.chip.hint',
      'messages.chip.dismiss',
      'messages.chip.cover',
      'messages.chip.task',
      'messages.chip.privateLesson',
      'messages.chip.makeup',
      'messages.chip.absence',
      'messages.chip.whichDancer',
      'messages.chip.makeup.heading',
      'messages.chip.makeup.nothing',
      'messages.chip.close',
      'messages.chip.failed',
      'messages.card.open',
      'messages.card.yes',
      'messages.card.no',
      'messages.card.withdrawn',
      'messages.card.countered',
      'messages.card.unasked',
      'messages.card.gone',
      'messages.card.link',
    ],
  },
  {
    /*
     * Not her website, and it used to sit in the middle of it under a heading
     * reading `import` (Q3, answered as (a)). Genuinely editable under D-144 —
     * it describes Jackrabbit's interface, so a moved menu must not be a deploy
     * — but it belongs at the bottom, named for what it is.
     */
    heading: 'Import screen instructions',
    note: 'Not part of your website. These are the notes on the import screen.',
    keys: [
      'import.guide.heading',
      'import.guide.intro',
      'import.guide.families',
      'import.guide.students',
      'import.guide.classes',
      'import.guide.staff',
      'import.guide.enrollments',
      'import.guide.warning',
    ],
  },
]

/**
 * The heading for blocks this file has never heard of.
 *
 * <b>An unreachable field is a worse failure than an unordered one.</b> A block
 * added to the seeder without being placed here still has to be editable, and
 * has to say why it looks odd — the alternative is a field that exists in the
 * database, renders somewhere, and cannot be found on the screen that exists to
 * find fields.
 */
export const UNPLACED_HEADING = 'Not placed on a page yet'

export const UNPLACED_NOTE =
  'These are editable, but nobody has recorded where they appear. Ask, rather than guessing.'

/*
 * There was a `MANIFEST_KEYS` export here, described as "used by the drift
 * test". Nothing imported it — the drift test is `ContentManifestTests.cs` and
 * reads this file as text. An export with no caller reads as "something checks
 * this", which is the opposite of true, so it is gone rather than left as
 * reassurance.
 */
